"use client";

import { useCallback } from "react";
import { usePathname } from "next/navigation";
import { useAuthContext } from "@/context/authContext";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";
import { useCollectionsStore } from "@/stores/collectionsStore";
import {
  type PriceMonitorRequest,
  type PriceMonitorRequestOptions,
  usePriceMonitorRequestStore,
} from "@/stores/priceMonitorRequestStore";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";
import { useUiStore } from "@/stores/uiStore";
import { useNativeSignIn } from "./useNativeSignIn";

const extractFavoriteId = (
  entity?: { favoriteId?: number | null; favorite_id?: number | null } | null
) => {
  const favoriteId = entity?.favoriteId ?? entity?.favorite_id;
  return typeof favoriteId === "number" && favoriteId > 0 ? favoriteId : null;
};

const resolveFavoriteId = (item: SearchResultItem, favoriteId?: number | null) => {
  const explicitFavoriteId = extractFavoriteId({ favoriteId });
  if (explicitFavoriteId) return explicitFavoriteId;

  const directFavoriteId = extractFavoriteId(
    item as SearchResultItem & { favoriteId?: number; favorite_id?: number }
  );
  if (directFavoriteId) return directFavoriteId;

  const collectionItem = useCollectionsStore
    .getState()
    .activeCollectionItems.find((candidate) => candidate.id === item.id);

  return extractFavoriteId(
    collectionItem as (SearchResultItem & { favoriteId?: number; favorite_id?: number }) | undefined
  );
};

export const openAuthenticatedPriceMonitor = ({ item, options }: PriceMonitorRequest) => {
  const { initialized, tokensRemaining } = useTokenBudgetStore.getState();
  const { openKurboNoTokensNag, openPriceMonitor } = useUiStore.getState().actions;

  if (TOKEN_UI_ENABLED && initialized && tokensRemaining === 0) {
    openKurboNoTokensNag();
    return;
  }

  openPriceMonitor(item, {
    favoriteId: resolveFavoriteId(item, options?.favoriteId),
    source: options?.source,
  });
};

export function usePriceMonitorRequest() {
  const { isLoaded, isSignedIn } = useAuthContext();
  const { openSignIn } = useNativeSignIn();
  const pathname = usePathname();

  const requestPriceMonitor = useCallback(
    (item: SearchResultItem, options?: PriceMonitorRequestOptions) => {
      const request: PriceMonitorRequest = { item, options };
      const requestActions = usePriceMonitorRequestStore.getState().actions;

      if (!isLoaded || !isSignedIn) {
        const uiState = useUiStore.getState();
        const pendingRequest = requestActions.queue(request, {
          pathname,
          sourceModalOpen:
            options?.source === "item"
              ? uiState.itemModalOpen
              : options?.source === "favorites"
                ? uiState.favoritesModalOpen
                : false,
        });

        if (isLoaded) {
          requestActions.markSignInStarted(pendingRequest.requestToken);
          void openSignIn().catch(() => requestActions.clear(pendingRequest.requestToken));
        }
        return;
      }

      requestActions.clear();
      openAuthenticatedPriceMonitor(request);
    },
    [isLoaded, isSignedIn, openSignIn, pathname]
  );

  return { requestPriceMonitor };
}
